import * as React from 'react'
import { Icons, Typography } from '@lacedltd/laced-components'
import axios from 'axios'
import useSWR from 'swr'
import * as RadixNav from '@radix-ui/react-navigation-menu'
import { useCookie } from 'react-use'
import tokens from '@lacedltd/design-tokens'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import {
  useUser,
  useLocale,
  useViewport,
  useTouchDevice,
  useTranslation
} from 'hooks'
import { withTranslations, withGrowthbook } from 'helpers'
import Logo from '../../../assets/images/logo.svg'
import ChristmasLogo from '../../../assets/images/logo-christmas.svg'
import Search from '../AlgoliaSearch/Search'
import SiteSettings from '../essential/SiteSettings'
import GlobalNotificationBanner from '../shared/GlobalNotificationBanner'
import CartItem from './CartItem'
import DesktopNavMenu from './DesktopNavMenu'
import MobileNav from './MobileNav'
import * as S from './TopNav.styles'
import type { LacedAvailableLocales } from 'types/localisation'
import type { TopNavProps, Browse, ShopNavBrand } from './TopNav.types'

const TopNav = ({
  numberOfCartItems = 0,
  algoliaUserId,
  geoLocale,
  showGlobalNotice,
  showGlobalReadMore
}: TopNavProps) => {
  const { user, fetching, isLoggedIn } = useUser()
  const { localiseUrl } = useLocale()
  const { t } = useTranslation()
  const [value] = useCookie('preferred_locale')
  const { width } = useViewport()
  const { isTouch } = useTouchDevice()
  const christmasLogo = useFeatureIsOn('christmas_logo')

  const { data: megaMenu } = useSWR<Browse>(
    // we made an api change, but users had cached responses, which broke the menu.
    // adding v=1 to cache bust, then all new requests will have a new cache under v=1
    localiseUrl('/api/mega_menu/1?v=1', { localeAsParam: true }),
    (url: string) =>
      axios
        .get(url, { headers: { 'Key-Inflection': 'camel' } })
        .then((res) => res.data.megaMenu),
    {}
  )

  const { data: shopNav } = useSWR<ShopNavBrand[]>(
    localiseUrl('/api/shop_nav', { localeAsParam: true }),
    (url: string) =>
      axios
        .get(url, { headers: { 'Key-Inflection': 'camel' } })
        .then((res) => res.data.shopNav),
    {}
  )

  const isMobile = width <= tokens.breakpoints.l || isTouch

  const [showMobileNav, setShowMobileNav] = React.useState(false)

  const hideMobileNav = () => setShowMobileNav(false)

  const isCheckout = window.location.pathname.includes('checkout')

  return (
    <S.TopNavBar
      orientation="horizontal"
      delayDuration={0}
      skipDelayDuration={0}
    >
      {showGlobalNotice && (
        <GlobalNotificationBanner showGlobalReadMore={showGlobalReadMore} />
      )}
      <S.MenuInnerContainer>
        <S.RightBlock>
          {!isCheckout ? (
            <RadixNav.Item data-testid="hamburger-menu-trigger">
              <S.MenuIconButton
                onClick={() => {
                  setShowMobileNav((prev) => !prev)
                }}
                data-state={showMobileNav ? 'open' : 'closed'}
              >
                {showMobileNav ? <Icons.Cross /> : <Icons.Hamburger />}
              </S.MenuIconButton>
            </RadixNav.Item>
          ) : null}

          <RadixNav.Item>
            <S.LogoWrapper
              href={localiseUrl('/')}
              christmasLogo={christmasLogo}
            >
              <img
                src={christmasLogo ? ChristmasLogo : Logo}
                alt={t('shared.laced_home')}
                height={20}
                width={90}
              />
            </S.LogoWrapper>
          </RadixNav.Item>
        </S.RightBlock>

        {!isCheckout ? <DesktopNavMenu browse={megaMenu} /> : null}

        {!isCheckout ? (
          <S.LeftBlock>
            <S.ActionIcon>
              {!fetching && (
                <SiteSettings
                  geoLocale={geoLocale as LacedAvailableLocales}
                  preferredLocale={user?.preferred_locale || value}
                  isLoggedIn={isLoggedIn}
                />
              )}
            </S.ActionIcon>
            {isMobile && (
              <S.ActionIcon>
                <RadixNav.Link
                  href={localiseUrl('/account')}
                  title={t('shared.account')}
                >
                  <Icons.Profile />
                </RadixNav.Link>
              </S.ActionIcon>
            )}

            <S.SearchActionIcon>
              <Search userId={algoliaUserId} />
            </S.SearchActionIcon>

            <S.ActionIcon>
              <RadixNav.Link href={localiseUrl('/cart')}>
                <CartItem numberOfCartItems={numberOfCartItems} />
              </RadixNav.Link>
            </S.ActionIcon>
          </S.LeftBlock>
        ) : (
          <S.StripeBadge>
            <Typography.Text as="p" variant="caption">
              {t('react_components.payment.secure_checkout')}{' '}
            </Typography.Text>
            {isMobile ? null : <Icons.PoweredByStripe />}
          </S.StripeBadge>
        )}
      </S.MenuInnerContainer>
      <S.NavPanel data-state={showMobileNav ? 'open' : 'closed'}>
        <MobileNav showSiteSettings isOpen={showMobileNav} shopNav={shopNav} />
      </S.NavPanel>
      <S.Backdrop
        data-state={showMobileNav ? 'open' : 'closed'}
        onClick={hideMobileNav}
      />
    </S.TopNavBar>
  )
}

export default withGrowthbook(withTranslations(TopNav))
